import {
  Box,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { formatDateAndTime } from '@shared/utils/date';
import { useChatbot } from '@/feature/chatbot/useChatbot';
import { useChatbotHistory } from '@/feature/chatbot/useChatbotHistory';
import { useTexts } from '@/feature/lang';
import { Icon } from '@/icon/Icon';
import Tooltip from '../Tooltip/Tooltip';
import Translate from '../Translate';
import { ConversationMenu } from './components/ConversationMenu';
import { CreateNewButton } from './components/CreateNewButton';
import { HISTORY_WIDTH } from './styles';

const HistoryContainer = styled('div')({
  width: HISTORY_WIDTH,
  flexShrink: 0,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
});

const HistoryHeader = styled('div')(({ theme }) => ({
  padding: theme.spacing(0.5, 1),
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  color: theme.palette.text.secondary,
  width: '100%',
}));

const HistoryList = styled(List)({
  overflow: 'auto',
  flexGrow: 1,
});

const HistoryListItem = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})<{ isActive?: boolean }>(({ theme, isActive }) => ({
  marginBottom: theme.spacing(0.5),
  backgroundColor: isActive ? theme.palette.action.selected : 'transparent',
  '&:hover': {
    backgroundColor: isActive
      ? theme.palette.action.selected
      : theme.palette.action.hover,
  },
  position: 'relative',
}));

const ActionButtons = styled('div')(({ theme }) => ({
  position: 'absolute',
  right: theme.spacing(1),
  top: '50%',
  transform: 'translateY(-50%)',
  display: 'flex',
  gap: theme.spacing(0.5),
  opacity: 0,
  width: 0,
  overflow: 'hidden',
  transition: 'opacity 0.2s ease, width 0.2s ease',
  '.MuiListItemButton-root:hover &': {
    opacity: 1,
    width: 'auto',
  },
}));

const EmptyStateContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(2),
  height: '100%',
  textAlign: 'center',
  color: theme.palette.text.secondary,
}));

interface HistoryProps {
  onClose?: () => void;
}

export const History = ({ onClose }: HistoryProps) => {
  const texts = useTexts().component.chatbot;
  const { conversation, assignConversation } = useChatbot();
  const { history } = useChatbotHistory();

  return (
    <HistoryContainer>
      <HistoryHeader>
        <CreateNewButton />
        {onClose && (
          <Tooltip title={texts.actions.closeMenu}>
            <IconButton size="small" onClick={onClose}>
              <Icon name="close" />
            </IconButton>
          </Tooltip>
        )}
      </HistoryHeader>

      <HistoryList>
        {history.length === 0 ? (
          <EmptyStateContainer>
            <Icon
              name="history"
              size="xlarge"
              style={{ opacity: 0.5, marginBottom: 8 }}
            />
            <Typography variant="body2">
              <Translate text={texts.history.noHistory.text} />
            </Typography>
          </EmptyStateContainer>
        ) : (
          history
            .slice()
            .sort(
              (a, b) =>
                new Date(b.createdAt).getTime() -
                new Date(a.createdAt).getTime()
            )
            .map((item) => (
              <ListItem key={item.id} disablePadding>
                <HistoryListItem
                  onClick={() => assignConversation(item)}
                  isActive={item.id === conversation?.id}
                >
                  <Box component="div" sx={{ width: '100%' }}>
                    <Typography variant="subtitle2" noWrap>
                      {item.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {formatDateAndTime(item.createdAt)}
                    </Typography>
                  </Box>

                  <ActionButtons>
                    <ConversationMenu conversationId={item.id} />
                  </ActionButtons>
                </HistoryListItem>
              </ListItem>
            ))
        )}
      </HistoryList>
    </HistoryContainer>
  );
};
